import { useState, useEffect } from 'react';

// Module-level Set survives SPA navigation but resets on actual page
// reload (JS re-evaluates), so it correctly distinguishes the two.
const clearedOnReload = new Set();

// Tracks the previous pathname so we can detect "unrelated" navigation.
let previousPath = typeof window !== 'undefined' ? window.location.pathname : '';

/**
 * usePageState — persists filter/pagination state in sessionStorage.
 *
 * - Hard refresh (F5 / Ctrl+R) → state is cleared (fresh defaults)
 * - Navigate to a related page (detail/edit/create) and back → restored
 * - Navigate to an unrelated page and back → filters are cleared
 * - Manually click "Clear filters" → just reset state as normal
 *
 * @param {string}   key            Unique key per list page, e.g. 'tagManager'
 * @param {object}   defaults       Default state values
 * @param {string[]} relatedPaths   Route prefixes that should keep filters alive
 *                                  e.g. ['/tickets'] keeps state when navigating
 *                                  between /tickets, /tickets/view/5, /tickets/create
 * @returns {[object, function]}    [state, setState]
 */
function usePageState(key, defaults, relatedPaths) {
  const storageKey = `pageState_${key}`;

  // On hard refresh, browser sets navigation type to 'reload'.
  // Clear saved state so the page starts fresh — but only once per key
  // per page-load so that returning via SPA navigation keeps the state.
  if (typeof window !== 'undefined' && !clearedOnReload.has(storageKey)) {
    const navEntries = window.performance?.getEntriesByType?.('navigation');
    if (navEntries?.[0]?.type === 'reload') {
      sessionStorage.removeItem(storageKey);
    }
    clearedOnReload.add(storageKey);
  }

  // If the user arrived from an unrelated page, clear saved state so
  // filters don't bleed across unrelated sections.
  if (relatedPaths && typeof window !== 'undefined') {
    const cameFromRelated = relatedPaths.some(
      (prefix) => previousPath.startsWith(prefix)
    ); 
    if (!cameFromRelated) {
      sessionStorage.removeItem(storageKey);
    }
  }

  const [state, setStateInternal] = useState(() => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) {
        return { ...defaults, ...JSON.parse(saved) };
      }
    } catch {
      // ignore parse errors
    }
    return defaults;
  });

  // Persist to sessionStorage on every change
  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // ignore storage errors (private browsing quota, etc.)
    }
  }, [storageKey, state]);

  // Update previousPath when this component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      previousPath = window.location.pathname;
    };
  }, []);

  const setState = (updater) => {
    setStateInternal((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      return next;
    });
  };

  return [state, setState];
}

export default usePageState;
